import { Link } from "react-router-dom";
import { ArrowLeft, Save, Loader2 } from "lucide-react";
import { Controller } from "react-hook-form";
import { useEditPage } from "@/hooks/useEditPage";
import ErrorBanner from "@/components/common/ErrorBanner";
import { RichTextEditor } from "@/components/editor/RichTextEditor";

interface SeasonMonth {
  score: "best" | "good" | "poor";
  note?: string;
}

interface CountryForm {
  countryCode: string;
  countryName: string;
  visaPolicy: string;
  visaFreeDays: number | null;
  visaFee: string;
  visaUrl: string;
  visaNotes: string;
  passportMinValidityMonths: number | null;
  vaccinationRequired: string;
  healthNotes: string;
  arrivalCard: string;
  arrivalCardUrl: string;
  arrivalCardWindowDays: number | null;
  arrivalCardNotes: string;
  arrivalCardGroupMax: number | null;
  arrivalCardArtifactsText: string;
  arrivalCardChannel: string;
  arrivalCardAutoEnabled: boolean;
  visaFilingLeadDays: number | null;
  seasonality: Record<string, SeasonMonth> | null;
  currencyInfo: string;
  plugs: string;
  tipping: string;
  safetyNotes: string;
  practicalNotes: string;
  isActive: boolean;
}

const EMPTY: CountryForm = {
  countryCode: "",
  countryName: "",
  visaPolicy: "",
  visaFreeDays: null,
  visaFee: "",
  visaUrl: "",
  visaNotes: "",
  passportMinValidityMonths: null,
  vaccinationRequired: "",
  healthNotes: "",
  arrivalCard: "",
  arrivalCardUrl: "",
  arrivalCardWindowDays: null,
  arrivalCardNotes: "",
  arrivalCardGroupMax: null,
  arrivalCardArtifactsText: "",
  arrivalCardChannel: "manual",
  arrivalCardAutoEnabled: false,
  visaFilingLeadDays: null,
  seasonality: null,
  currencyInfo: "",
  plugs: "",
  tipping: "",
  safetyNotes: "",
  practicalNotes: "",
  isActive: true,
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const SCORE_STYLE: Record<string, string> = {
  best: "bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300",
  good: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300",
  poor: "bg-red-50 text-red-600 border-red-200 dark:bg-red-900/20 dark:text-red-300",
};

/** 12-month seasonality board: click a month to cycle best → good → poor → unset. */
function SeasonalityEditor({
  value,
  onChange,
}: {
  value: Record<string, SeasonMonth> | null;
  onChange: (v: Record<string, SeasonMonth> | null) => void;
}) {
  const cycle = (m: number) => {
    const key = String(m);
    const cur = value?.[key]?.score;
    const next =
      cur === "best" ? "good" : cur === "good" ? "poor" : cur === "poor" ? undefined : "best";
    const out = { ...(value ?? {}) };
    if (next) out[key] = { ...(out[key] ?? {}), score: next };
    else delete out[key];
    onChange(Object.keys(out).length ? out : null);
  };
  const setNote = (m: number, note: string) => {
    const key = String(m);
    if (!value?.[key]) return;
    onChange({ ...value, [key]: { ...value[key], note } });
  };

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {MONTHS.map((name, i) => {
        const m = i + 1;
        const entry = value?.[String(m)];
        return (
          <div
            key={m}
            className={`rounded-lg border p-2 ${
              entry ? SCORE_STYLE[entry.score] : "border-gray-200 dark:border-gray-700"
            }`}
          >
            <button
              type="button"
              onClick={() => cycle(m)}
              className="flex w-full items-center justify-between text-sm font-medium"
              title="Click to cycle best → good → poor → unset"
            >
              <span>{name}</span>
              <span className="text-xs uppercase">{entry?.score ?? "—"}</span>
            </button>
            {entry && (
              <input
                value={entry.note ?? ""}
                onChange={(e) => setNote(m, e.target.value)}
                placeholder="why (e.g. dry season)"
                className="mt-1 w-full rounded border-0 bg-white/60 px-1.5 py-0.5 text-xs text-gray-700 placeholder:text-gray-400 dark:bg-black/20 dark:text-gray-200"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Country KB edit page (Block 5.6 Řez 1) — visa / passport & health / arrival card /
 * seasonality / practical. Numbers and enums stay structured (the five consumers read
 * them as data); only the notes fields are rich text.
 */
export default function CountryKbEditPage() {
  const { isNew, isLoading, form, saveMutation, onSubmit, itemData } = useEditPage<CountryForm>({
    resourceKey: "country-kb-item",
    listKey: "country-kb",
    apiPath: "/api/admin/country-requirements",
    defaultValues: EMPTY,
    navigateTo: "/country-kb",
    transformOnLoad: (r) => ({
      countryCode: r.countryCode ?? "",
      countryName: r.countryName ?? "",
      visaPolicy: r.visaPolicy ?? "",
      visaFreeDays: r.visaFreeDays ?? null,
      visaFee: r.visaFee ?? "",
      visaUrl: r.visaUrl ?? "",
      visaNotes: r.visaNotes ?? "",
      passportMinValidityMonths: r.passportMinValidityMonths ?? null,
      vaccinationRequired: r.vaccinationRequired ?? "",
      healthNotes: r.healthNotes ?? "",
      arrivalCard: r.arrivalCard ?? "",
      arrivalCardUrl: r.arrivalCardUrl ?? "",
      arrivalCardWindowDays: r.arrivalCardWindowDays ?? null,
      arrivalCardNotes: r.arrivalCardNotes ?? "",
      arrivalCardGroupMax: r.arrivalCardGroupMax ?? null,
      // The API stores artifacts as string[]; the form edits them as comma-separated text.
      arrivalCardArtifactsText: (() => {
        const arts = (r as unknown as { arrivalCardArtifacts?: unknown }).arrivalCardArtifacts;
        return Array.isArray(arts) ? arts.join(", ") : "";
      })(),
      arrivalCardChannel: r.arrivalCardChannel ?? "manual",
      arrivalCardAutoEnabled: r.arrivalCardAutoEnabled ?? false,
      visaFilingLeadDays: r.visaFilingLeadDays ?? null,
      seasonality: r.seasonality ?? null,
      currencyInfo: r.currencyInfo ?? "",
      plugs: r.plugs ?? "",
      tipping: r.tipping ?? "",
      safetyNotes: r.safetyNotes ?? "",
      practicalNotes: r.practicalNotes ?? "",
      isActive: r.isActive ?? true,
    }),
    transformOnSave: (f) => ({
      ...f,
      countryCode: f.countryCode.toUpperCase(),
      visaPolicy: f.visaPolicy || null,
      // Nullable ints: ?? preserves null (|| would coerce it to 0 and fail .min checks).
      visaFreeDays: f.visaFreeDays ?? null,
      passportMinValidityMonths: f.passportMinValidityMonths ?? null,
      arrivalCard: f.arrivalCard || null,
      arrivalCardWindowDays: f.arrivalCardWindowDays ?? null,
      arrivalCardGroupMax: f.arrivalCardGroupMax ?? null,
      arrivalCardArtifactsText: undefined,
      arrivalCardArtifacts: f.arrivalCardArtifactsText
        ? f.arrivalCardArtifactsText
            .split(",")
            .map((a) => a.trim())
            .filter(Boolean)
        : null,
      visaFilingLeadDays: f.visaFilingLeadDays ?? null,
    }),
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = form;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/country-kb"
            className="rounded-lg p-2 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {isNew ? "New country" : `${itemData?.countryName ?? "Country"}`}
            </h1>
            <p className="mt-1 text-gray-600 dark:text-gray-400">
              Structured facts — offers, pre-departure pack and the AI read these as data.
            </p>
          </div>
        </div>
        <button type="submit" form="ckb-form" disabled={isSubmitting} className="btn-primary">
          {isSubmitting ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" /> Saving...
            </>
          ) : (
            <>
              <Save className="h-5 w-5" /> Save
            </>
          )}
        </button>
      </div>

      <ErrorBanner error={saveMutation.error} />

      <form id="ckb-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="card space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">Country code (ISO-2)</label>
              <input
                {...register("countryCode", {
                  required: "Required",
                  minLength: { value: 2, message: "2 letters" },
                  maxLength: { value: 2, message: "2 letters" },
                })}
                className="input font-mono uppercase"
                placeholder="VN"
                maxLength={2}
              />
              {errors.countryCode && (
                <p className="mt-1 text-sm text-red-500">{errors.countryCode.message}</p>
              )}
            </div>
            <div className="sm:col-span-2">
              <label className="label">Country name</label>
              <input
                {...register("countryName", { required: "Required" })}
                className="input"
                placeholder="Vietnam"
              />
              {errors.countryName && (
                <p className="mt-1 text-sm text-red-500">{errors.countryName.message}</p>
              )}
            </div>
            <div className="flex items-end pb-2">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input type="checkbox" {...register("isActive")} className="h-4 w-4 rounded" />
                Active
              </label>
            </div>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Visa</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">Policy</label>
              <select {...register("visaPolicy")} className="input">
                <option value="">— Unknown —</option>
                <option value="visa_free">Visa-free</option>
                <option value="visa_on_arrival">Visa on arrival</option>
                <option value="e_visa">E-visa</option>
                <option value="embassy">Embassy visa</option>
              </select>
            </div>
            <div>
              <label className="label">Visa-free days</label>
              <input
                type="number"
                {...register("visaFreeDays", { valueAsNumber: true })}
                className="input"
                placeholder="45"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Fee</label>
              <input
                {...register("visaFee")}
                className="input"
                placeholder="25 USD (single-entry e-visa)"
              />
            </div>
          </div>
          <div>
            <label className="label">Official URL</label>
            <input {...register("visaUrl")} className="input" placeholder="https://…" />
          </div>
          <div>
            <label className="label">Notes</label>
            <Controller
              name="visaNotes"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Application steps, processing time, multi-entry rules…"
                />
              )}
            />
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Passport &amp; health
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Passport min validity (months)</label>
              <input
                type="number"
                {...register("passportMinValidityMonths", { valueAsNumber: true })}
                className="input"
                placeholder="6"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Vaccination</label>
              <input
                {...register("vaccinationRequired")}
                className="input"
                placeholder="None required; Hepatitis A/B recommended"
              />
            </div>
          </div>
          <div>
            <label className="label">Health notes</label>
            <textarea {...register("healthNotes")} rows={2} className="input" />
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Arrival card</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
            <div>
              <label className="label">Type</label>
              <select {...register("arrivalCard")} className="input">
                <option value="">— Unknown —</option>
                <option value="none">None</option>
                <option value="on_arrival">Paper on arrival</option>
                <option value="online">Online form</option>
              </select>
            </div>
            <div>
              <label className="label">Opens (days before)</label>
              <input
                type="number"
                {...register("arrivalCardWindowDays", { valueAsNumber: true })}
                className="input"
                placeholder="7"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Form URL</label>
              <input {...register("arrivalCardUrl")} className="input" placeholder="https://…" />
            </div>
          </div>
          <div>
            <label className="label">Notes</label>
            <textarea {...register("arrivalCardNotes")} rows={2} className="input" />
          </div>
          <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
            <h3 className="mb-1 text-sm font-semibold text-gray-900 dark:text-white">
              Entry engine
            </h3>
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
              Drives the per-traveller filing queue on departures. Auto-filing stays off until the
              browser flow for this country is verified (Block 5.4 PoC).
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div>
                <label className="label">Group submission (pax)</label>
                <input
                  type="number"
                  {...register("arrivalCardGroupMax", { valueAsNumber: true })}
                  className="input"
                  placeholder="10"
                />
                <p className="mt-1 text-xs text-gray-500">TDAC/SGAC 10; per-pax portals 1</p>
              </div>
              <div>
                <label className="label">Visa task opens (days before)</label>
                <input
                  type="number"
                  {...register("visaFilingLeadDays", { valueAsNumber: true })}
                  className="input"
                  placeholder="30"
                />
                <p className="mt-1 text-xs text-gray-500">e-visa lead time</p>
              </div>
              <div>
                <label className="label">Filing channel</label>
                <select {...register("arrivalCardChannel")} className="input">
                  <option value="manual">Manual (operator)</option>
                  <option value="browser">Browser (autopilot)</option>
                </select>
              </div>
              <div>
                <label className="label">Extra artifacts</label>
                <input
                  {...register("arrivalCardArtifactsText")}
                  className="input"
                  placeholder="selfie, health_declaration"
                />
                <p className="mt-1 text-xs text-gray-500">comma-separated</p>
              </div>
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <input
                type="checkbox"
                {...register("arrivalCardAutoEnabled")}
                className="h-4 w-4 rounded"
              />
              Auto-filing enabled (requires browser channel + client consent)
            </label>
          </div>
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Seasonality</h2>
          <p className="-mt-2 text-sm text-gray-500 dark:text-gray-400">
            Click a month to cycle best → good → poor → unset. The AI uses this to suggest a better
            month when an inquiry hits a poor season.
          </p>
          <Controller
            name="seasonality"
            control={control}
            render={({ field }) => (
              <SeasonalityEditor value={field.value} onChange={field.onChange} />
            )}
          />
        </div>

        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Practical</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="label">Currency</label>
              <input
                {...register("currencyInfo")}
                className="input"
                placeholder="VND; cards common in cities, cash elsewhere"
              />
            </div>
            <div>
              <label className="label">Plugs</label>
              <input {...register("plugs")} className="input" placeholder="A/C/F, 220 V" />
            </div>
            <div>
              <label className="label">Tipping</label>
              <input
                {...register("tipping")}
                className="input"
                placeholder="Not expected; guides ~5 USD/day appreciated"
              />
            </div>
          </div>
          <div>
            <label className="label">Safety notes</label>
            <textarea {...register("safetyNotes")} rows={2} className="input" />
          </div>
          <div>
            <label className="label">Practical notes</label>
            <Controller
              name="practicalNotes"
              control={control}
              render={({ field }) => (
                <RichTextEditor
                  content={field.value || ""}
                  onChange={field.onChange}
                  placeholder="SIM cards, transport, dress code…"
                />
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
